import {
  getAudioBlob,
  type RecordingItem,
  STORAGE_KEY,
} from '@affine/core/desktop/pages/workspace/recordings/global-recording-manager';

export interface GoogleDriveConfig {
  accessToken: string;
  clientId: string;
  autoBackup: boolean;
  backupFolderId: string;
  recordingsFolderId: string;
  docsFolderId: string;
  lastSyncTime?: number;
  userEmail?: string;
  userName?: string;
}

const GDRIVE_CONFIG_KEY = 'cao_oi_google_drive_config_v1';

export function getGoogleDriveConfig(): GoogleDriveConfig {
  try {
    if (typeof localStorage === 'undefined') {
      return {
        accessToken: '',
        clientId: '',
        autoBackup: false,
        backupFolderId: '',
        recordingsFolderId: '',
        docsFolderId: '',
      };
    }
    const raw = localStorage.getItem(GDRIVE_CONFIG_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (err) {
    console.warn('Failed to read Google Drive config from localStorage:', err);
  }
  return {
    accessToken: '',
    clientId: '',
    autoBackup: false,
    backupFolderId: '',
    recordingsFolderId: '',
    docsFolderId: '',
  };
}

export function saveGoogleDriveConfig(
  patch: Partial<GoogleDriveConfig>
): GoogleDriveConfig {
  const current = getGoogleDriveConfig();
  const updated: GoogleDriveConfig = { ...current, ...patch };
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(GDRIVE_CONFIG_KEY, JSON.stringify(updated));
    }
  } catch (err) {
    console.warn('Failed to save Google Drive config:', err);
  }
  return updated;
}

/**
 * Verify if Google Drive Access Token is valid and retrieve user information
 */
export async function verifyGoogleDriveToken(token: string): Promise<{
  valid: boolean;
  user?: { email: string; name: string };
  error?: string;
}> {
  if (!token) {
    return { valid: false, error: 'No token provided' };
  }
  try {
    const res = await fetch(
      'https://www.googleapis.com/drive/v3/about?fields=user',
      {
        headers: {
          Authorization: `Bearer ${token.trim()}`,
        },
      }
    );
    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      return {
        valid: false,
        error:
          errJson.error?.message ||
          `Google Drive API returned status ${res.status}`,
      };
    }
    const data = await res.json();
    return {
      valid: true,
      user: {
        email: data.user?.emailAddress || 'Connected User',
        name: data.user?.displayName || 'Google Drive User',
      },
    };
  } catch (err: any) {
    return {
      valid: false,
      error: err.message || 'Network connection to Google Drive failed',
    };
  }
}

/**
 * Load Google Identity Services (GIS) client script dynamically
 */
function loadGoogleIdentityScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('No window'));
    if ((window as any).google?.accounts?.oauth2) {
      return resolve();
    }
    const existing = document.getElementById('google-gsi-script');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Google GIS script'))
      );
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load Google Identity Services'));
    document.head.append(script);
  });
}

/**
 * Trigger Google OAuth popup via Google Identity Services
 */
export async function initiateGISSignIn(clientId: string): Promise<string> {
  if (!clientId) {
    throw new Error('Google OAuth Client ID is required');
  }
  await loadGoogleIdentityScript();
  const google = (window as any).google;
  if (!google?.accounts?.oauth2) {
    throw new Error('Google Identity Services not available');
  }

  return new Promise((resolve, reject) => {
    try {
      const tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: clientId.trim(),
        scope:
          'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive',
        callback: (resp: any) => {
          if (resp.error) {
            reject(new Error(resp.error_description || resp.error));
            return;
          }
          if (resp.access_token) {
            resolve(resp.access_token);
          } else {
            reject(new Error('No access token returned by Google'));
          }
        },
      });
      tokenClient.requestAccessToken({ prompt: 'consent' });
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Helper to find or create a Google Drive folder
 */
export async function getOrCreateFolder(
  token: string,
  folderName: string,
  parentId?: string
): Promise<string> {
  let query = `name = '${folderName.replace(/'/g, "\\'")}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`;
  if (parentId) {
    query += ` and '${parentId}' in parents`;
  }

  const listRes = await fetch(
    `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(
      query
    )}&fields=files(id,name)`,
    {
      headers: {
        Authorization: `Bearer ${token.trim()}`,
      },
    }
  );

  if (listRes.ok) {
    const listData = await listRes.json();
    if (listData.files && listData.files.length > 0) {
      return listData.files[0].id;
    }
  }

  // Create folder if not found
  const body: Record<string, any> = {
    name: folderName,
    mimeType: 'application/vnd.google-apps.folder',
  };
  if (parentId) {
    body.parents = [parentId];
  }

  const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token.trim()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!createRes.ok) {
    const err = await createRes.json().catch(() => ({}));
    throw new Error(
      err.error?.message || `Failed to create folder ${folderName}`
    );
  }

  const createData = await createRes.json();
  return createData.id;
}

/**
 * Upload a file directly to Google Drive via multipart upload
 */
export async function uploadFileToDrive(
  token: string,
  name: string,
  mimeType: string,
  contentBlob: Blob,
  parentFolderId?: string
): Promise<{ id: string; name: string }> {
  const metadata: Record<string, any> = {
    name,
    mimeType,
  };
  if (parentFolderId) {
    metadata.parents = [parentFolderId];
  }

  const boundary = '-------314159265358979323846';
  const delimiter = `\r\n--${boundary}\r\n`;
  const closeDelimiter = `\r\n--${boundary}--`;

  const metadataPart = `${delimiter}Content-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(
    metadata
  )}`;
  const mediaHeader = `${delimiter}Content-Type: ${mimeType}\r\n\r\n`;

  const metadataBlob = new Blob([metadataPart]);
  const headerBlob = new Blob([mediaHeader]);
  const closeBlob = new Blob([closeDelimiter]);

  const multipartBody = new Blob([
    metadataBlob,
    headerBlob,
    contentBlob,
    closeBlob,
  ]);

  const res = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token.trim()}`,
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body: multipartBody,
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Failed to upload file ${name}`);
  }

  return await res.json();
}

/**
 * Backup all local lecture recordings (audio files + transcripts) to Google Drive
 */
export async function backupAllRecordingsToDrive(
  onProgress?: (status: string, current: number, total: number) => void
): Promise<{ success: boolean; count: number; error?: string }> {
  const config = getGoogleDriveConfig();
  if (!config.accessToken) {
    return { success: false, count: 0, error: 'Chưa kết nối Google Drive' };
  }

  try {
    onProgress?.('Đang kết nối Google Drive...', 0, 1);

    // 1. Root backup folder: "AFFiNE - Cáo Ơi Backups"
    const rootFolderId = await getOrCreateFolder(
      config.accessToken,
      'AFFiNE - Cáo Ơi Backups'
    );
    // 2. Subfolder: "Recordings"
    const recFolderId = await getOrCreateFolder(
      config.accessToken,
      'Recordings',
      rootFolderId
    );

    saveGoogleDriveConfig({
      backupFolderId: rootFolderId,
      recordingsFolderId: recFolderId,
    });

    // 3. Load recordings from archive
    let list: RecordingItem[] = [];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) list = JSON.parse(raw);
    } catch {}

    if (list.length === 0) {
      onProgress?.('Không có bản ghi âm nào để sao lưu.', 1, 1);
      return { success: true, count: 0 };
    }

    let uploadedCount = 0;
    const totalFiles = list.length * 2; // Audio + Transcript

    for (const item of list) {
      const safeTitle = (item.title || `Lecture_${item.id}`)
        .replace(/[\\/*?:"<>|]/g, '_')
        .slice(0, 50);
      const dateStr = new Date(item.createdAt).toISOString().slice(0, 10);

      // Upload Audio Blob
      try {
        const audioBlob = await getAudioBlob(item.id);
        if (audioBlob && audioBlob.size > 0) {
          onProgress?.(
            `Đang tải lên âm thanh: ${safeTitle}...`,
            uploadedCount,
            totalFiles
          );
          await uploadFileToDrive(
            config.accessToken,
            `${safeTitle}_${dateStr}.webm`,
            'audio/webm',
            audioBlob,
            recFolderId
          );
          uploadedCount++;
        }
      } catch (audioErr) {
        console.warn(`Failed to upload audio for ${item.id}:`, audioErr);
      }

      // Upload Transcript Text
      if (item.transcript) {
        try {
          onProgress?.(
            `Đang tải lên bản dịch bài giảng: ${safeTitle}...`,
            uploadedCount,
            totalFiles
          );
          const transcriptBlob = new Blob(
            [
              `BÀI GIẢNG: ${item.title}\nThời gian: ${new Date(
                item.createdAt
              ).toLocaleString('vi-VN')}\nThời lượng: ${
                item.durationSeconds
              } giây\n\nNỘI DUNG GHI CHÚ BÀI GIẢNG:\n\n${item.transcript}`,
            ],
            { type: 'text/plain;charset=utf-8' }
          );
          await uploadFileToDrive(
            config.accessToken,
            `${safeTitle}_${dateStr}_transcript.txt`,
            'text/plain',
            transcriptBlob,
            recFolderId
          );
          uploadedCount++;
        } catch (txtErr) {
          console.warn(`Failed to upload transcript for ${item.id}:`, txtErr);
        }
      }
    }

    saveGoogleDriveConfig({ lastSyncTime: Date.now() });
    onProgress?.(`Đã sao lưu thành công ${uploadedCount} tệp!`, totalFiles, totalFiles);
    return { success: true, count: uploadedCount };
  } catch (err: any) {
    console.error('Google Drive backup error:', err);
    return {
      success: false,
      count: 0,
      error: err.message || 'Lỗi khi tải lên Google Drive',
    };
  }
}

/**
 * Backup all workspace documents as Markdown & JSON summary to Google Drive
 */
export async function backupDocsToDrive(
  docs: Array<{ id: string; title: string; content?: string }>,
  onProgress?: (status: string, current: number, total: number) => void
): Promise<{ success: boolean; count: number; error?: string }> {
  const config = getGoogleDriveConfig();
  if (!config.accessToken) {
    return { success: false, count: 0, error: 'Chưa kết nối Google Drive' };
  }

  try {
    onProgress?.('Đang kết nối thư mục Google Drive...', 0, docs.length);

    const rootFolderId = await getOrCreateFolder(
      config.accessToken,
      'AFFiNE - Cáo Ơi Backups'
    );
    const docsFolderId = await getOrCreateFolder(
      config.accessToken,
      'Documents',
      rootFolderId
    );

    saveGoogleDriveConfig({
      backupFolderId: rootFolderId,
      docsFolderId,
    });

    let uploaded = 0;
    const dateStr = new Date().toISOString().slice(0, 10);

    for (const doc of docs) {
      const safeTitle = (doc.title || `Document_${doc.id}`)
        .replace(/[\\/*?:"<>|]/g, '_')
        .slice(0, 50);

      onProgress?.(
        `Đang sao lưu tài liệu: ${safeTitle}...`,
        uploaded,
        docs.length
      );

      const content =
        doc.content ||
        `# ${doc.title || 'Untitled'}\n\n*Sao lưu từ AFFiNE Cáo Ơi ngày ${new Date().toLocaleDateString(
          'vi-VN'
        )}*`;

      const docBlob = new Blob([content], {
        type: 'text/markdown;charset=utf-8',
      });

      try {
        await uploadFileToDrive(
          config.accessToken,
          `${safeTitle}_${dateStr}.md`,
          'text/markdown',
          docBlob,
          docsFolderId
        );
        uploaded++;
      } catch (uploadErr) {
        console.warn(`Failed to upload doc ${doc.id}:`, uploadErr);
      }
    }

    saveGoogleDriveConfig({ lastSyncTime: Date.now() });
    onProgress?.(`Đã sao lưu thành công ${uploaded} tài liệu!`, docs.length, docs.length);
    return { success: true, count: uploaded };
  } catch (err: any) {
    console.error('Docs backup error:', err);
    return {
      success: false,
      count: 0,
      error: err.message || 'Lỗi khi sao lưu tài liệu',
    };
  }
}

/**
 * Automatically backup a single new recording right after it finishes recording
 */
export async function autoBackupSingleRecording(
  item: RecordingItem,
  audioBlob?: Blob | null
): Promise<void> {
  const config = getGoogleDriveConfig();
  if (!config.autoBackup || !config.accessToken) {
    return;
  }

  try {
    const rootFolderId = await getOrCreateFolder(
      config.accessToken,
      'AFFiNE - Cáo Ơi Backups'
    );
    const recFolderId = await getOrCreateFolder(
      config.accessToken,
      'Recordings',
      rootFolderId
    );

    const safeTitle = (item.title || `Lecture_${item.id}`)
      .replace(/[\\/*?:"<>|]/g, '_')
      .slice(0, 50);
    const dateStr = new Date(item.createdAt).toISOString().slice(0, 10);

    // Upload audio if present
    const blobToUpload = audioBlob || (await getAudioBlob(item.id));
    if (blobToUpload && blobToUpload.size > 0) {
      await uploadFileToDrive(
        config.accessToken,
        `${safeTitle}_${dateStr}.webm`,
        'audio/webm',
        blobToUpload,
        recFolderId
      );
    }

    // Upload transcript if present
    if (item.transcript) {
      const transcriptBlob = new Blob(
        [
          `BÀI GIẢNG: ${item.title}\nThời gian: ${new Date(
            item.createdAt
          ).toLocaleString('vi-VN')}\nThời lượng: ${
            item.durationSeconds
          } giây\n\nNỘI DUNG GHI CHÚ BÀI GIẢNG:\n\n${item.transcript}`,
        ],
        { type: 'text/plain;charset=utf-8' }
      );
      await uploadFileToDrive(
        config.accessToken,
        `${safeTitle}_${dateStr}_transcript.txt`,
        'text/plain',
        transcriptBlob,
        recFolderId
      );
    }

    saveGoogleDriveConfig({ lastSyncTime: Date.now() });
    console.log(`[GoogleDriveSync] Auto-backed up recording: ${safeTitle}`);
  } catch (err) {
    console.warn('[GoogleDriveSync] Auto-backup failed:', err);
  }
}
